import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export interface EvolutionDiagnosisContext {
  evolutionCount: number;
  /** Primeira evolução cronológica (com ou sem diagnóstico). */
  firstEvolutionId: string | null;
  /** Primeira evolução que já tem diagnóstico — fonte do perfil. */
  anchoredDiagnosisEvolutionId: string | null;
  anchoredDiagnosis: string | null;
  anchoredDiagnosisCode: string | null;
}

/** @deprecated use EvolutionDiagnosisContext */
export type EvolutionFirstMeta = EvolutionDiagnosisContext;

export function evolutionRowHasDiagnosis(row?: {
  diagnosis?: string | null;
  diagnosis_code?: string | null;
} | null): boolean {
  return Boolean(
    String(row?.diagnosis || '').trim()
    || String(row?.diagnosis_code || '').trim(),
  );
}

export function patientHasPrimaryDiagnosis(patient?: {
  primary_diagnosis?: string | null;
  primary_diagnosis_code?: string | null;
} | null): boolean {
  return Boolean(
    String(patient?.primary_diagnosis || '').trim()
    || String(patient?.primary_diagnosis_code || '').trim(),
  );
}

function sortEvolutionsChronologically(list: any[]): any[] {
  return [...list].sort((a: any, b: any) => {
    const dateCmp = String(a.evolution_date || '').localeCompare(String(b.evolution_date || ''));
    if (dateCmp !== 0) return dateCmp;
    const timeCmp = String(a.evolution_time || '').localeCompare(String(b.evolution_time || ''));
    if (timeCmp !== 0) return timeCmp;
    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
  });
}

export function buildEvolutionDiagnosisContext(list: any[]): EvolutionDiagnosisContext {
  if (!list.length) {
    return {
      evolutionCount: 0,
      firstEvolutionId: null,
      anchoredDiagnosisEvolutionId: null,
      anchoredDiagnosis: null,
      anchoredDiagnosisCode: null,
    };
  }

  const sorted = sortEvolutionsChronologically(list);
  const anchored = sorted.find((row) => evolutionRowHasDiagnosis(row));

  return {
    evolutionCount: list.length,
    firstEvolutionId: sorted[0]?.id ? String(sorted[0].id) : null,
    anchoredDiagnosisEvolutionId: anchored?.id ? String(anchored.id) : null,
    anchoredDiagnosis: anchored?.diagnosis ? String(anchored.diagnosis).trim() : null,
    anchoredDiagnosisCode: anchored?.diagnosis_code ? String(anchored.diagnosis_code).trim() : null,
  };
}

/**
 * Diagnóstico só na 1ª evolução sem diagnóstico ainda definido,
 * ou ao editar a evolução que já carrega o diagnóstico do perfil.
 */
export function shouldCapturePrimaryDiagnosis(params: {
  patient?: { primary_diagnosis?: string | null; primary_diagnosis_code?: string | null } | null;
  context: EvolutionDiagnosisContext;
  activeEvolutionId?: string | null;
}): boolean {
  const { patient, context, activeEvolutionId } = params;
  const profileHasDiagnosis =
    patientHasPrimaryDiagnosis(patient)
    || Boolean(context.anchoredDiagnosis || context.anchoredDiagnosisCode);

  const anchorId =
    context.anchoredDiagnosisEvolutionId
    || context.firstEvolutionId;

  if (!profileHasDiagnosis) {
    if (context.evolutionCount === 0) return true;
    return Boolean(
      activeEvolutionId
      && context.firstEvolutionId
      && activeEvolutionId === context.firstEvolutionId,
    );
  }

  return Boolean(activeEvolutionId && anchorId && activeEvolutionId === anchorId);
}

export function formatPrimaryDiagnosisLabel(
  patient?: {
    primary_diagnosis?: string | null;
    primary_diagnosis_code?: string | null;
  } | null,
  context?: EvolutionDiagnosisContext | null,
  recentEvolutions?: any[] | null,
): string | null {
  const fromPatient = patientHasPrimaryDiagnosis(patient)
    ? formatDiagnosisPair(patient?.primary_diagnosis, patient?.primary_diagnosis_code)
    : null;
  if (fromPatient) return fromPatient;

  if (context && (context.anchoredDiagnosis || context.anchoredDiagnosisCode)) {
    return formatDiagnosisPair(context.anchoredDiagnosis, context.anchoredDiagnosisCode);
  }

  if (recentEvolutions?.length) {
    const built = buildEvolutionDiagnosisContext(recentEvolutions);
    if (built.anchoredDiagnosis || built.anchoredDiagnosisCode) {
      return formatDiagnosisPair(built.anchoredDiagnosis, built.anchoredDiagnosisCode);
    }
  }

  return null;
}

export function formatDiagnosisPair(text?: string | null, code?: string | null): string | null {
  const label = String(text || '').trim();
  const cid = String(code || '').trim();
  if (!label && !cid) return null;
  if (label && cid) return `${label} · ${cid}`;
  return label || cid;
}

export async function fetchEvolutionDiagnosisContext(patientId: string): Promise<EvolutionDiagnosisContext> {
  const { data } = await axios.get(getApiUrl(`/clinic/evolutions/patient/${patientId}/timeline`));
  const list = Array.isArray(data) ? data : (data?.data || []);
  return buildEvolutionDiagnosisContext(list);
}

/** @deprecated use fetchEvolutionDiagnosisContext */
export async function fetchEvolutionFirstMeta(patientId: string): Promise<EvolutionDiagnosisContext> {
  return fetchEvolutionDiagnosisContext(patientId);
}
